import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  Loader2,
  Phone,
  Mail,
  MessageSquare,
  MapPin,
  HelpCircle,
  CalendarDays,
  Users,
  TrendingUp,
  Clock,
  ChevronRight,
  Filter,
  Download,
} from "lucide-react";

import { crmContactsReport, type ContactsReportResult } from "@/lib/crm.functions";
import { crmListAssignableReps } from "@/lib/crm-followup.functions";
import { downloadCsv } from "@/lib/csv";
import { Button } from "@/components/ui/button";


const BS = {
  primary: "#0d6efd",
  cardBorder: "#dee2e6",
  muted: "#6c757d",
  dark: "#212529",
  green: "#198754",
};

const CHANNEL_ICONS: Record<string, React.ReactNode> = {
  call: <Phone className="h-3.5 w-3.5" />,
  email: <Mail className="h-3.5 w-3.5" />,
  whatsapp: <MessageSquare className="h-3.5 w-3.5" />,
  visit: <MapPin className="h-3.5 w-3.5" />,
  other: <HelpCircle className="h-3.5 w-3.5" />,
};

const RANGE_PRESETS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 14 days", days: 14 },
  { label: "Last 30 days", days: 30 },
  { label: "This month", days: 0 },
  { label: "Last 90 days", days: 90 },
  { label: "Custom", days: -1 },
];

const CUSTOM = -1;

function helsinkiDateString(d = new Date()) {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Helsinki" }).format(d);
}

export const Route = createFileRoute("/crm/reports/contacts")({
  head: () => ({
    meta: [
      { title: "Contacts Report — CRM" },
      { name: "description", content: "CRM contacts report by time period and sales rep." },
      { property: "og:title", content: "Contacts Report — CRM" },
      { property: "og:description", content: "CRM contacts report by time period and sales rep." },
    ],
  }),
  component: ContactsReportPage,
  errorComponent: ({ error }) => (
    <div className="grid min-h-screen place-items-center px-6 text-center text-sm text-muted-foreground">
      {(error as Error | undefined)?.message ?? "Failed to load contacts report"}
    </div>
  ),
});

function ContactsReportPage() {
  const fetchReport = useServerFn(crmContactsReport);
  const listReps = useServerFn(crmListAssignableReps);
  const [days, setDays] = useState<number>(30);
  const [repId, setRepId] = useState<string>("");
  const [customFrom, setCustomFrom] = useState<string>("");
  const [customTo, setCustomTo] = useState<string>("");

  const range = useMemo(() => {
    const now = new Date();
    if (days === CUSTOM) {
      const from = customFrom
        ? new Date(`${customFrom}T00:00:00+03:00`)
        : new Date(new Date(now).setDate(now.getDate() - 30));
      const to = customTo
        ? new Date(`${customTo}T23:59:59+03:00`)
        : new Date(`${helsinkiDateString(now)}T23:59:59+03:00`);
      return { from: from.toISOString(), to: to.toISOString() };
    }
    if (days === 0) {
      // This month
      const helsinki = helsinkiDateString(now);
      const from = new Date(`${helsinki.slice(0, 7)}-01T00:00:00+03:00`);
      const to = new Date(`${helsinki}T23:59:59+03:00`);
      return { from: from.toISOString(), to: to.toISOString() };
    }
    const to = new Date(now);
    const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [days, customFrom, customTo]);

  const reportQ = useQuery({
    queryKey: ["crm", "contacts-report", range.from, range.to, repId || "all"],
    queryFn: () => fetchReport({ data: { ...range, rep_user_id: repId || null } }),
  });

  const repsQ = useQuery({
    queryKey: ["crm", "assignable-reps"],
    queryFn: () => listReps(),
  });

  const data = reportQ.data;

  if (reportQ.isLoading || repsQ.isLoading) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (reportQ.error || !data) {
    return (
      <div className="p-6 text-sm text-red-600">
        {(reportQ.error as any)?.message ?? "Failed to load contacts report"}
      </div>
    );
  }

  const maxDay = Math.max(1, ...data.by_day.map((d) => d.count));
  const repOptions = repsQ.data?.reps ?? [];

  const rangeTag = `${data.from.slice(0, 10)}_to_${data.to.slice(0, 10)}`;
  const repTag = repId ? `_${(repOptions.find((r) => r.user_id === repId)?.display_name || repId.slice(0, 8)).replace(/\s+/g, "-")}` : "";

  const exportContacts = () => {
    downloadCsv(`contacts_${rangeTag}${repTag}`, [
      ["Date (Helsinki)", "Time", "Client", "Client ID", "Salesperson", "Salesperson Email", "Channel", "Summary"],
      ...data.recent.map((c) => {
        const d = new Date(c.occurred_at);
        return [
          new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Helsinki" }).format(d),
          new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Helsinki", hour: "2-digit", minute: "2-digit" }).format(d),
          c.client_name,
          c.client_id,
          c.actor_display_name || "",
          c.actor_email || "",
          c.channel,
          c.summary || "",
        ];
      }),
    ]);
  };

  const exportByRep = () => {
    const channels = Array.from(new Set(data.by_rep.flatMap((r) => Object.keys(r.channels)))).sort();
    downloadCsv(`contacts_by_rep_${rangeTag}`, [
      ["Salesperson", "Email", "Contacts", "Clients Touched", "Follow-ups Done", "Meetings", "Last Contact", ...channels],
      ...data.by_rep.map((r) => [
        r.display_name || r.user_id,
        r.email || "",
        r.count,
        r.clients_touched,
        r.followups_done,
        r.meetings,
        r.last_contact_at ? new Date(r.last_contact_at).toISOString() : "",
        ...channels.map((c) => r.channels[c] ?? 0),
      ]),
    ]);
  };

  const exportByDay = () => {
    downloadCsv(`contacts_by_day_${rangeTag}${repTag}`, [
      ["Date", "Contacts"],
      ...data.by_day.map((d) => [d.date, d.count]),
    ]);
  };

  return (
    <div className="bg-[#f6f7fb] h-full overflow-y-auto pb-10">
      <div className="sticky top-0 z-20 rounded-2xl border border-border bg-background/95 backdrop-blur">
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 px-3 py-2.5 sm:py-3 max-w-6xl mx-auto">
          <Link to="/crm/reports" className="rounded-md p-2 hover:bg-muted" aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <h1 className="truncate text-sm sm:text-base font-bold" style={{ letterSpacing: "-0.01em" }}>
              Contacts Report
            </h1>
            <p className="hidden sm:block text-[11px] mt-0.5" style={{ color: BS.muted }}>
              How many contacts were made, and who made them.
            </p>
          </div>
          <Button size="sm" onClick={exportContacts} className="h-9 gap-1.5 shrink-0">
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export CSV</span>
          </Button>
        </div>
      </div>


      <div className="px-3 sm:px-5 py-4 sm:py-6 max-w-6xl mx-auto space-y-4 sm:space-y-5">
        {/* Filters */}
        <div className="rounded-2xl border bg-white p-3 sm:p-4 flex flex-col gap-3" style={{ borderColor: BS.cardBorder }}>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Filter className="h-4 w-4 shrink-0" style={{ color: BS.muted }} />
              <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap sm:overflow-visible">
                {RANGE_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => setDays(p.days)}
                    className={`shrink-0 px-3 py-2 sm:py-1 rounded-full text-xs font-medium border transition-colors whitespace-nowrap ${
                      days === p.days
                        ? "bg-[#0d6efd] text-white border-[#0d6efd]"
                        : "bg-white text-[#212529] hover:bg-[#f8f9fc]"
                    }`}
                    style={days === p.days ? undefined : { borderColor: BS.cardBorder }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {data.is_admin && repOptions.length > 0 && (
              <div className="grid grid-cols-[auto_minmax(0,1fr)] sm:flex items-center gap-2 shrink-0">
                <span className="text-[11px] font-semibold" style={{ color: BS.muted }}>
                  Rep:
                </span>
                <select
                  value={repId}
                  onChange={(e) => setRepId(e.target.value)}
                  className="h-10 sm:h-9 w-full sm:w-auto rounded-md border bg-white px-2 text-sm"
                  style={{ borderColor: BS.cardBorder, color: BS.dark }}
                >
                  <option value="">All reps</option>
                  {repOptions.map((r) => (
                    <option key={r.user_id} value={r.user_id}>
                      {r.display_name || r.user_id.slice(0, 8)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {days === CUSTOM && (
            <div className="flex flex-col sm:flex-row gap-3 pt-1 border-t" style={{ borderColor: BS.cardBorder }}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-[11px] font-semibold shrink-0" style={{ color: BS.muted }}>
                  From
                </span>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  max={customTo || helsinkiDateString()}
                  className="h-10 sm:h-9 w-full rounded-md border bg-white px-2 text-sm"
                  style={{ borderColor: BS.cardBorder, color: BS.dark }}
                />
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-[11px] font-semibold shrink-0" style={{ color: BS.muted }}>
                  To
                </span>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  min={customFrom}
                  max={helsinkiDateString()}
                  className="h-10 sm:h-9 w-full rounded-md border bg-white px-2 text-sm"
                  style={{ borderColor: BS.cardBorder, color: BS.dark }}
                />
              </div>
              <div className="text-[11px]" style={{ color: BS.muted }}>
                {customFrom && customTo
                  ? `${new Date(customFrom).toLocaleDateString()} – ${new Date(customTo).toLocaleDateString()}`
                  : "Pick a date range to see the custom list"}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-1 border-t" style={{ borderColor: BS.cardBorder }}>
            <span className="text-[11px] font-semibold pt-2" style={{ color: BS.muted }}>
              Export:
            </span>
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs mt-2" onClick={exportContacts}>
              <Download className="h-3.5 w-3.5" /> Contacts ({data.recent.length})
            </Button>
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs mt-2" onClick={exportByRep}>
              <Download className="h-3.5 w-3.5" /> By Salesperson ({data.by_rep.length})
            </Button>
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs mt-2" onClick={exportByDay}>
              <Download className="h-3.5 w-3.5" /> By Day ({data.by_day.length})
            </Button>
          </div>
        </div>



        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3">
          <SummaryCard icon={<Clock className="h-4 w-4" />} label="Today" value={data.summary.today} />
          <SummaryCard icon={<CalendarDays className="h-4 w-4" />} label="Yesterday" value={data.summary.yesterday} />
          <SummaryCard icon={<TrendingUp className="h-4 w-4" />} label="Last 7 Days" value={data.summary.last7} />
          <SummaryCard icon={<CalendarDays className="h-4 w-4" />} label="Last 30 Days" value={data.summary.last30} />
          <SummaryCard icon={<Users className="h-4 w-4" />} label="This Month" value={data.summary.thisMonth} />
          <SummaryCard icon={<TrendingUp className="h-4 w-4" />} label="Selected Range" value={data.summary.total} accent />
        </div>

        {/* Chart */}
        <div className="bg-white border rounded-2xl p-4 sm:p-5" style={{ borderColor: BS.cardBorder }}>
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <h2 className="text-sm font-bold" style={{ color: BS.dark }}>
              Contacts per Day
            </h2>
            <span className="text-[11px]" style={{ color: BS.muted }}>
              {data.by_day.length} days · {data.summary.total} contacts
            </span>
          </div>
          {data.by_day.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No contacts in this range.</p>
          ) : (
            (() => {
              const n = data.by_day.length;
              const labelEvery = Math.ceil(n / (n > 45 ? 8 : n > 20 ? 10 : n));
              const gap = n > 60 ? 1 : n > 30 ? 2 : 3;
              return (
                <>
                  <div className="flex items-end h-32 sm:h-40 border-b" style={{ gap, borderColor: BS.cardBorder }}>
                    {data.by_day.map((d) => (
                      <div
                        key={d.date}
                        className="flex-1 h-full flex flex-col justify-end min-w-0"
                        title={`${d.date}: ${d.count} contact${d.count === 1 ? "" : "s"}`}
                      >
                        <div
                          className="w-full rounded-t transition-all hover:opacity-80"
                          style={{
                            height: d.count ? `${Math.max((d.count / maxDay) * 100, 8)}%` : "2px",
                            background: d.count > 0 ? "linear-gradient(180deg, #198754 0%, #34c47a 100%)" : "#e9ecef",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex mt-1.5" style={{ gap }}>
                    {data.by_day.map((d, i) => (
                      <div key={d.date} className="flex-1 min-w-0 text-center">
                        {i % labelEvery === 0 && (
                          <span className="text-[9px] sm:text-[10px] whitespace-nowrap" style={{ color: BS.muted }}>
                            {d.date.slice(5)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[10px]" style={{ color: BS.muted }}>
                    <span>{data.by_day[0]?.date}</span>
                    <span>Peak: {maxDay}/day</span>
                    <span>{data.by_day[n - 1]?.date}</span>
                  </div>
                </>
              );
            })()
          )}

        </div>

        {/* Team activity */}
        <div className="bg-white border rounded-2xl overflow-hidden" style={{ borderColor: BS.cardBorder }}>
          <div className="px-4 sm:px-5 py-3 border-b" style={{ borderColor: BS.cardBorder }}>
            <h2 className="text-sm font-bold" style={{ color: BS.dark }}>
              Sales Team Activity
            </h2>
            <p className="text-[11px] mt-0.5" style={{ color: BS.muted }}>
              Who is following up, how they reached out, and their latest activity
            </p>
          </div>
          {data.by_rep.length === 0 ? (
            <div className="p-5 text-sm text-center" style={{ color: BS.muted }}>
              No activity in this range.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: BS.cardBorder }}>
              {data.by_rep.map((r: ContactsReportResult["by_rep"][number], idx: number) => {
                const label = r.display_name || r.email || `Rep ${r.user_id.slice(0, 8)}`;
                const initial = (label || "?").charAt(0).toUpperCase();
                const channels = Object.entries(r.channels).sort((a, b) => b[1] - a[1]);
                return (
                  <li key={r.user_id} className="px-4 py-3 sm:px-5">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <div
                          className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-sm font-bold text-white"
                          style={{ background: "linear-gradient(135deg,#0d6efd,#4f8dff)" }}
                        >
                          {initial}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-semibold truncate" style={{ color: BS.dark }}>
                              {label}
                            </span>
                            {idx === 0 && data.by_rep.length > 1 && r.count > 0 && (
                              <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                                Top
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] truncate" style={{ color: BS.muted }}>
                            {r.email ? `${r.email} · ` : ""}
                            {r.last_contact_at
                              ? `Last activity ${new Date(r.last_contact_at).toLocaleString([], {
                                  dateStyle: "medium",
                                  timeStyle: "short",
                                })}`
                              : "No contact logged yet"}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-1 rounded-xl bg-[#f8f9fc] p-2 sm:flex sm:items-center sm:gap-4 sm:bg-transparent sm:p-0 sm:shrink-0">
                        <RepStat label="Contacts" value={r.count} accent />
                        <RepStat label="Clients" value={r.clients_touched} />
                        <RepStat label="Follow-ups" value={r.followups_done} />
                        <RepStat label="Meetings" value={r.meetings} />
                      </div>
                    </div>

                    {channels.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5 ml-0 sm:ml-11">
                        {channels.map(([ch, n]) => (
                          <span
                            key={ch}
                            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
                            style={{ background: "#f1f5f9", color: BS.dark }}
                          >
                            {CHANNEL_ICONS[ch] ?? CHANNEL_ICONS.other}
                            {ch} · {n}
                          </span>
                        ))}
                      </div>
                    )}
                    {data.is_admin && (
                      <div className="mt-2 ml-0 sm:ml-11">
                        <button
                          onClick={() => setRepId(repId === r.user_id ? "" : r.user_id)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold hover:underline"
                          style={{ color: BS.primary }}
                        >
                          {repId === r.user_id ? "Clear filter" : "View only this rep"}
                          <ChevronRight className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:gap-4">


          {/* Recent contacts */}
          <div className="lg:col-span-2 bg-white border rounded-2xl overflow-hidden" style={{ borderColor: BS.cardBorder }}>
            <div className="px-4 sm:px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: BS.cardBorder }}>
              <div>
                <h2 className="text-sm font-bold" style={{ color: BS.dark }}>
                  Recent Contacts
                </h2>
                <p className="text-[11px] mt-0.5" style={{ color: BS.muted }}>
                  Latest 200 logged contacts
                </p>
              </div>
              <span className="text-[11px] font-mono px-2 py-0.5 rounded-full" style={{ background: "rgba(13,110,253,0.1)", color: BS.primary }}>
                {data.recent.length}
              </span>
            </div>
            {data.recent.length === 0 ? (
              <div className="p-5 text-sm text-center" style={{ color: BS.muted }}>
                No contacts in this range.
              </div>
            ) : (
              <>
                {/* Mobile: card list */}
                <ul className="divide-y lg:hidden" style={{ borderColor: BS.cardBorder }}>
                  {data.recent.map((c) => (
                    <li key={c.id} className="px-4 py-3">
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                        <Link
                          to="/crm/$clientId"
                          params={{ clientId: c.client_id }}
                          className="truncate text-sm font-semibold hover:underline"
                          style={{ color: BS.primary }}
                        >
                          {c.client_name}
                        </Link>
                        <span className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize" style={{ background: "#f1f5f9", color: BS.dark }}>
                          {CHANNEL_ICONS[c.channel] ?? CHANNEL_ICONS.other}
                          {c.channel}
                        </span>
                      </div>
                      <div className="mt-1 text-[11px] truncate" style={{ color: BS.muted }}>
                        {c.actor_display_name || c.actor_email || c.actor_user_id.slice(0, 8)}
                        {" · "}
                        {new Date(c.occurred_at).toLocaleDateString()}{" "}
                        {new Date(c.occurred_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      {c.summary && (
                        <p className="mt-1 text-sm line-clamp-2" style={{ color: BS.dark }}>
                          {c.summary}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>

                {/* Desktop: table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left" style={{ background: "#f8f9fc" }}>
                        <th className="px-4 py-2.5 text-xs font-semibold" style={{ color: BS.muted }}>When</th>
                        <th className="px-3 py-2.5 text-xs font-semibold" style={{ color: BS.muted }}>Salesperson</th>
                        <th className="px-3 py-2.5 text-xs font-semibold" style={{ color: BS.muted }}>Client</th>
                        <th className="px-3 py-2.5 text-xs font-semibold" style={{ color: BS.muted }}>Channel</th>
                        <th className="px-3 py-2.5 text-xs font-semibold" style={{ color: BS.muted }}>Summary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent.map((c) => (
                        <tr key={c.id} className="border-t hover:bg-[#f8f9fc]" style={{ borderColor: BS.cardBorder }}>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            <div className="text-xs font-medium" style={{ color: BS.dark }}>
                              {new Date(c.occurred_at).toLocaleDateString()}
                            </div>
                            <div className="text-[10px]" style={{ color: BS.muted }}>
                              {new Date(c.occurred_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="text-sm font-medium" style={{ color: BS.dark }}>
                              {c.actor_display_name || c.actor_email || c.actor_user_id.slice(0, 8)}
                            </div>
                            {c.actor_email && c.actor_display_name && (
                              <div className="text-[11px]" style={{ color: BS.muted }}>
                                {c.actor_email}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <Link
                              to="/crm/$clientId"
                              params={{ clientId: c.client_id }}
                              className="text-sm font-medium hover:underline"
                              style={{ color: BS.primary }}
                            >
                              {c.client_name}
                            </Link>
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ background: "#f1f5f9", color: BS.dark }}>
                              {CHANNEL_ICONS[c.channel] ?? CHANNEL_ICONS.other}
                              {c.channel}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="text-sm line-clamp-2" style={{ color: BS.muted }}>
                              {c.summary}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div
      className="rounded-2xl border p-3 sm:p-4"
      style={{
        borderColor: BS.cardBorder,
        background: accent ? "rgba(25,135,84,0.06)" : "#fff",
      }}
    >
      <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide" style={{ color: BS.muted }}>
        {icon}
        {label}
      </div>
      <div className="mt-1 text-xl sm:text-2xl font-extrabold tracking-tight tabular-nums" style={{ color: accent ? BS.green : BS.dark }}>
        {value}
      </div>
    </div>
  );
}

function RepStat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className="text-center">
      <div
        className="text-base font-extrabold tabular-nums leading-none"
        style={{ color: accent ? BS.green : BS.dark }}
      >
        {value}
      </div>
      <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: BS.muted }}>
        {label}
      </div>
    </div>
  );
}
